import type { ClipCandidate, TranscriptSentence } from '@/shared/api/app-types'
import { appApiBaseUrl } from '@/shared/api/app-client'
import type { CompositionInputProps, ExtendedExportSettings } from '@/types/export'
import type { TimelineTrack, VideoItem, AudioItem, TimelineItem, ImageItem } from '@/types/timeline'
import type { ProjectTimeline } from '@/types/project'
import type { MediaMetadata } from '@/types/storage'
import type { RenderProgress, ClientRenderResult } from '@/features/export/utils/client-renderer'
import {
  mapToClientSettings,
  validateSettings,
  getSupportedCodecs,
  selectFallbackVideoCodec,
  getPreferredContainerForCodec,
} from '@/features/export/utils/client-renderer'
import { renderComposition } from '@/features/export/utils/client-render-engine'
import { buildCaptionTextItems } from '@/features/media-library/utils/caption-items'
import { getClipCaptionTemplate } from './clip-style-templates'
import { buildCaptionRenderConfig, getCaptionStyleConfig } from '@/features/app-shell/components/caption-style-config'
import type { CaptionTextItemTemplate } from '@/features/media-library/utils/caption-items'

// ---- 编辑器路径所需（workspace 初始化 + 项目构建）----
import { getWorkspaceHandleRecord, queryHandlePermission } from '@/infrastructure/storage/handles-db'
import { getWorkspaceRoot, setWorkspaceRoot } from '@/infrastructure/storage/workspace-fs/root'
import { createAssistantProject } from '@/features/assistant/tools/project-tools'
import { importLocalMedia } from '@/features/assistant/tools/media-tools'
import { updateProject } from '@/infrastructure/storage'
import { createDefaultClassicTracks } from '@/features/timeline/utils/classic-tracks'
import { buildDroppedMediaTimelineItem } from '@/features/timeline/utils/dropped-media'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import type { TimelineTrack as TTrack } from '@/types/timeline'
import type { TemplatePreviewConfig } from '@/features/app-shell/components/template-preview'

export interface ClipRenderConfig {
  /** 源视频 URL（/uploads/... 或 https://...） */
  src: string
  /** 字幕样式 key（captionLibraryStyles 的 key） */
  style?: string
  /** 'portrait' | 'landscape' | 'square' */
  orientation?: string
  /** '720' | '1080' | '4k' */
  resolution?: string
  /** 品牌模板名称，仅用于展示 */
  brandTemplateName?: string | null
  brandTemplateId?: number | null
  /** 品牌模板里的字幕配置快照 */
  captionConfig?: Record<string, unknown> | null
  templateConfig?: Record<string, unknown> | null
  /** 源视频尺寸；下载链路会在渲染前补齐，使 contain 排版与页面预览一致。 */
  sourceWidth?: number
  sourceHeight?: number
}

export interface ClipRenderResult {
  blob: Blob
  mimeType: string
  duration: number
  fileSize: number
  fileName: string
}

export interface SourceEditorProjectConfig {
  src: string
  title?: string
}

export interface FileEditorProjectConfig {
  handle: FileSystemFileHandle
  title?: string
}

/** 方向 → 画布尺寸（短边对齐分辨率） */
function getCanvasDimensions(
  orientation?: string,
  resolution?: string,
): { width: number; height: number } {
  // 720p 短边 1280，1080p 短边 1920，4k 短边 3840
  const shortSide =
    resolution === '4k' ? 3840 : resolution === '1080' ? 1920 : 1280 // 720 default
  if (orientation === 'landscape') {
    return { width: shortSide, height: Math.round((shortSide * 9) / 16) }
  }
  if (orientation === 'square') {
    return { width: shortSide, height: shortSide }
  }
  // portrait default
  return { width: Math.round((shortSide * 9) / 16), height: shortSide }
}

/** 词级转录数据 */
interface TranscriptWord {
  text: string
  startMs: number
  endMs: number
  sentenceId: number
}

type TemplateVisualConfig = Pick<
  TemplatePreviewConfig,
  'backgroundColor' | 'background' | 'backgroundEnabled' | 'logos' | 'logoEnabled' | 'intro' | 'outro'
>

type TemplateAssetRef = {
  publicUrl?: string
  fileName?: string
  durationMs?: number
  imageWidth?: number
  imageHeight?: number
  position?: string
  scale?: string
  x?: number
  y?: number
  width?: number
  height?: number
}

/** 字幕分句的最大时长（秒），超过则强制断句 */
const MAX_CAPTION_DURATION_SEC = 5
/** 字幕分句的最大字符数，超过则强制断句 */
const MAX_CAPTION_CHARS = 40

/**
 * 用词级时间戳构建精细字幕分段。
 *
 * 通义听悟返回的 sentences 可能是段落级（每段 20-40 秒），直接用作字幕会导致
 * 一大段文字堆在一起。这里用 words 数组按标点/时长/字符数重新分句，确保每条
 * 字幕 2-5 秒、不超过 40 字。
 *
 * 返回的 segments 的 start/end 是源视频绝对秒。
 */
export function buildCaptionSegmentsFromWords(
  words: TranscriptWord[],
  clipStartMs: number,
  clipEndMs: number,
): Array<{ text: string; start: number; end: number }> {
  if (!words || words.length === 0) return []

  // 过滤出与片段范围重叠的词
  const clipWords = words.filter(
    (w) => w.startMs <= clipEndMs + 500 && w.endMs >= clipStartMs - 500,
  )
  if (clipWords.length === 0) return []

  const segments: Array<{ text: string; start: number; end: number }> = []
  let currentWords: TranscriptWord[] = []

  const flush = () => {
    if (currentWords.length === 0) return
    const text = currentWords.map((w) => w.text).join('').trim()
    if (text.length > 0) {
      const firstWord = currentWords[0]
      const lastWord = currentWords[currentWords.length - 1]
      if (firstWord && lastWord) {
        segments.push({
          text,
          start: firstWord.startMs / 1000,
          end: lastWord.endMs / 1000,
        })
      }
    }
    currentWords = []
  }

  for (const word of clipWords) {
    currentWords.push(word)
    const joined = currentWords.map((w) => w.text).join('')
    const firstWord = currentWords[0]
    const lastWord = currentWords[currentWords.length - 1]
    const duration = firstWord && lastWord
      ? (lastWord.endMs - firstWord.startMs) / 1000
      : 0

    // 断句条件：遇到句末标点、超过最大时长、超过最大字符数
    const endsWithPunctuation = /[。！？!?.；;，,、]$/.test(word.text.trim())
    if (endsWithPunctuation || duration >= MAX_CAPTION_DURATION_SEC || joined.length >= MAX_CAPTION_CHARS) {
      flush()
    }
  }
  flush() // 处理最后剩余的词

  return segments
}

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function deriveProjectTitleFromSource(src: string, fallback = '视频编辑项目'): string {
  try {
    const parsed = new URL(src)
    const last = parsed.pathname.split('/').filter(Boolean).at(-1)
    if (!last) return fallback
    return decodeURIComponent(last).slice(0, 60) || fallback
  } catch {
    return src.slice(0, 60) || fallback
  }
}

/** 将 /uploads/... 相对路径补全为后端完整 URL */
function resolveSourceUrl(src: string): string {
  if (src.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${src}`
  }
  return src
}

function parseTemplateVisualConfig(
  templateConfig?: Record<string, unknown> | null,
): TemplateVisualConfig | null {
  if (!templateConfig) return null
  return templateConfig as unknown as TemplateVisualConfig
}

function getTemplateAssetUrl(asset: TemplateAssetRef | null | undefined): string | null {
  const url = typeof asset?.publicUrl === 'string' ? asset.publicUrl : ''
  if (!url) return null
  return resolveSourceUrl(url)
}

function getTemplateDurationFrames(
  asset: TemplateAssetRef | null | undefined,
  fps: number,
  fallbackDurationSeconds = 0,
): number {
  const durationSeconds = typeof asset?.durationMs === 'number'
    ? asset.durationMs / 1000
    : fallbackDurationSeconds
  return Math.max(0, Math.round(durationSeconds * fps))
}

type ProbedVideoMetadata = {
  durationMs: number
  width: number
  height: number
}

/**
 * 模板配置在上传时只保存了 URL，历史模板没有 durationMs。
 * 导出前读取一次媒体 metadata，才能把片头、片尾真正纳入合成时长。
 */
async function probeVideoMetadata(src: string | null): Promise<ProbedVideoMetadata | null> {
  if (!src || typeof document === 'undefined') return null

  return new Promise((resolve) => {
    const video = document.createElement('video')
    let settled = false
    const finish = (value: ProbedVideoMetadata | null) => {
      if (settled) return
      settled = true
      video.removeAttribute('src')
      video.load()
      resolve(value)
    }
    const timeout = window.setTimeout(() => finish(null), 10_000)
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      window.clearTimeout(timeout)
      const durationMs = Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0
      finish({ durationMs, width: video.videoWidth, height: video.videoHeight })
    }
    video.onerror = () => {
      window.clearTimeout(timeout)
      finish(null)
    }
    video.src = src
  })
}

async function probeImageMetadata(src: string | null): Promise<{ width: number; height: number } | null> {
  if (!src || typeof Image === 'undefined') return null
  return new Promise((resolve) => {
    const image = new Image()
    let settled = false
    const finish = (value: { width: number; height: number } | null) => {
      if (settled) return
      settled = true
      resolve(value)
    }
    const timeout = window.setTimeout(() => finish(null), 10_000)
    image.onload = () => {
      window.clearTimeout(timeout)
      finish({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      window.clearTimeout(timeout)
      finish(null)
    }
    image.src = src
  })
}

function getLogoInitialPosition(position: string | undefined): { left: number; top: number } {
  switch (position) {
    case 'top-left': return { left: 5, top: 5 }
    case 'top-right': return { left: 85, top: 5 }
    case 'bottom-left': return { left: 5, top: 85 }
    case 'center': return { left: 42, top: 45 }
    case 'bottom-right':
    default:
      return { left: 85, top: 85 }
  }
}

function getLogoScaleWidth(scale: string | undefined): number {
  const map: Record<string, number> = { small: 8, medium: 12, large: 18 }
  return map[scale ?? 'medium'] ?? 12
}

function percentToPx(value: number | undefined, total: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.round((value / 100) * total)
}

function buildOverlayTransform(params: {
  canvasWidth: number
  canvasHeight: number
  sourceWidth?: number
  sourceHeight?: number
  x?: number
  y?: number
  widthPercent?: number
  heightPercent?: number
  fallbackWidthPercent: number
  fallbackLeftPercent: number
  fallbackTopPercent: number
}): NonNullable<ImageItem['transform']> {
  const {
    canvasWidth,
    canvasHeight,
    sourceWidth,
    sourceHeight,
    x,
    y,
    widthPercent,
    heightPercent,
    fallbackWidthPercent,
    fallbackLeftPercent,
    fallbackTopPercent,
  } = params

  const widthPx = percentToPx(widthPercent, canvasWidth, Math.round(canvasWidth * (fallbackWidthPercent / 100)))
  const aspectRatio =
    sourceWidth && sourceHeight && sourceWidth > 0 && sourceHeight > 0
      ? sourceWidth / sourceHeight
      : 1
  const heightPx = typeof heightPercent === 'number'
    ? percentToPx(heightPercent, canvasHeight, Math.round(widthPx / Math.max(aspectRatio, 0.01)))
    : Math.round(widthPx / Math.max(aspectRatio, 0.01))
  const leftPx = percentToPx(x, canvasWidth, Math.round(canvasWidth * (fallbackLeftPercent / 100)))
  const topPx = percentToPx(y, canvasHeight, Math.round(canvasHeight * (fallbackTopPercent / 100)))

  return {
    // Timeline transform 的 x/y 是相对画布中心的 item 中心点；模板编辑器
    // 存的是 CSS left/top 百分比。先换算到中心坐标，避免 logo 被推到画布外。
    x: leftPx + widthPx / 2 - canvasWidth / 2,
    y: topPx + heightPx / 2 - canvasHeight / 2,
    width: widthPx,
    height: heightPx,
    rotation: 0,
    opacity: 1,
  }
}

function createTrack(
  id: string,
  name: string,
  kind: 'video' | 'audio',
  order: number,
): TimelineTrack {
  return {
    id,
    name,
    kind,
    height: 56,
    locked: false,
    syncLock: true,
    visible: true,
    muted: false,
    solo: false,
    volume: 0,
    order,
    items: [],
  }
}

function mapFontWeight(weight: number | undefined): CaptionTextItemTemplate['fontWeight'] {
  if (!weight) return 'semibold'
  if (weight >= 700) return 'bold'
  if (weight >= 600) return 'semibold'
  return 'normal'
}

function buildCaptionTemplateFromConfig(config: ClipRenderConfig): CaptionTextItemTemplate {
  const baseTemplate = getClipCaptionTemplate(config.style)
  if (!config.captionConfig) {
    return baseTemplate
  }

  const styleBase = getCaptionStyleConfig(config.style ?? 'blue')
  const rendered = buildCaptionRenderConfig(styleBase, config.captionConfig)
  // 编辑器的 TextItem 不支持模板预览里的逐词高亮动画。对于 fill 高亮模板，
  // 将高亮色物化为基础文字色，确保编辑器和导出至少呈现正确的品牌主色。
  const color = rendered.highlightMode === 'fill' && rendered.highlightColor
    ? rendered.highlightColor
    : rendered.color

  return {
    ...baseTemplate,
    fontFamily: rendered.fontFamily,
    fontSize: rendered.fontSize,
    fontWeight: mapFontWeight(rendered.fontWeight),
    fontStyle: rendered.italic ? 'italic' : 'normal',
    color,
    backgroundColor: rendered.backgroundColor ?? baseTemplate.backgroundColor,
    textAlign: rendered.textAlign,
    lineHeight: rendered.lineHeight,
    letterSpacing: rendered.letterSpacing,
    stroke: rendered.strokeWidth > 0
      // 模板预览中的 5px 描边按 CSS 像素渲染；直接搬到编辑器画布会吞掉
      // 中文字形的填充色。按字号收敛，保留描边同时让黄色主色可见。
      ? { width: Math.min(rendered.strokeWidth, Math.max(1, Math.round(rendered.fontSize * 0.033))), color: rendered.strokeColor }
      : baseTemplate.stroke,
  }
}

async function importTemplateAsset(
  projectId: string,
  asset: TemplateAssetRef | null | undefined,
): Promise<MediaMetadata | null> {
  const url = getTemplateAssetUrl(asset)
  if (!url) return null
  const imported = await importLocalMedia({ projectId, url })
  return imported[0] ?? null
}

function buildTemplateCompositionVideoItem(params: {
  src: string
  label: string
  trackId: string
  from: number
  durationInFrames: number
  width: number
  height: number
}): VideoItem {
  return {
    id: crypto.randomUUID(),
    type: 'video',
    trackId: params.trackId,
    from: params.from,
    durationInFrames: params.durationInFrames,
    label: params.label,
    src: params.src,
    sourceStart: 0,
    sourceEnd: params.durationInFrames,
    sourceDuration: params.durationInFrames,
    sourceFps: 30,
    transform: {
      x: 0,
      y: 0,
      width: params.width,
      height: params.height,
      rotation: 0,
      opacity: 1,
    },
  }
}

function buildTemplateCompositionImageItem(params: {
  src: string
  label: string
  trackId: string
  from: number
  durationInFrames: number
  transform: NonNullable<ImageItem['transform']>
}): ImageItem {
  return {
    id: crypto.randomUUID(),
    type: 'image',
    trackId: params.trackId,
    from: params.from,
    durationInFrames: params.durationInFrames,
    label: params.label,
    src: params.src,
    sourceStart: 0,
    sourceEnd: params.durationInFrames,
    sourceDuration: params.durationInFrames,
    sourceFps: 30,
    transform: params.transform,
  }
}

function buildCoverTransform(
  canvasWidth: number,
  canvasHeight: number,
  sourceWidth?: number,
  sourceHeight?: number,
): NonNullable<ImageItem['transform']> {
  if (!sourceWidth || !sourceHeight || sourceWidth <= 0 || sourceHeight <= 0) {
    return { x: 0, y: 0, width: canvasWidth, height: canvasHeight, rotation: 0, opacity: 1 }
  }
  const scale = Math.max(canvasWidth / sourceWidth, canvasHeight / sourceHeight)
  return {
    x: 0,
    y: 0,
    width: Math.round(sourceWidth * scale),
    height: Math.round(sourceHeight * scale),
    rotation: 0,
    opacity: 1,
  }
}

function buildContainTransform(
  canvasWidth: number,
  canvasHeight: number,
  sourceWidth?: number,
  sourceHeight?: number,
  scale = 1,
): NonNullable<VideoItem['transform']> {
  if (!sourceWidth || !sourceHeight || sourceWidth <= 0 || sourceHeight <= 0) {
    return {
      x: 0,
      y: 0,
      width: Math.round(canvasWidth * scale),
      height: Math.round(canvasHeight * scale),
      rotation: 0,
      opacity: 1,
    }
  }
  const containScale = Math.min(canvasWidth / sourceWidth, canvasHeight / sourceHeight) * scale
  return {
    x: 0,
    y: 0,
    width: Math.round(sourceWidth * containScale),
    height: Math.round(sourceHeight * containScale),
    rotation: 0,
    opacity: 1,
  }
}

function buildTemplateEditorImageItem(params: {
  media: MediaMetadata
  trackId: string
  label: string
  timelineFps: number
  blobUrl: string
  canvasWidth: number
  canvasHeight: number
  from: number
  durationInFrames: number
  transform: NonNullable<ImageItem['transform']>
}): ImageItem {
  const item = buildDroppedMediaTimelineItem({
    media: params.media,
    mediaId: params.media.id,
    mediaType: 'image',
    label: params.label,
    timelineFps: params.timelineFps,
    blobUrl: params.blobUrl,
    canvasWidth: params.canvasWidth,
    canvasHeight: params.canvasHeight,
    placement: {
      trackId: params.trackId,
      from: params.from,
      durationInFrames: params.durationInFrames,
    },
  }) as ImageItem

  item.transform = params.transform
  return item
}

/**
 * 为单个片段手工构建 CompositionInputProps（不依赖项目/store/workspace）。
 *
 * 组合包含：
 *   - 一个视频轨 + 一个 VideoItem（sourceStart/sourceEnd = 片段帧范围，contain 适配画布）
 *   - 一个字幕轨 + TextItem[]（用片段范围内的转录句子 + 选定字幕样式）
 *
 * 直接传给 renderComposition 即可渲染 MP4，无需创建编辑器项目。
 */
export function buildClipComposition(
  clip: ClipCandidate,
  sentences: TranscriptSentence[],
  config: ClipRenderConfig,
  words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>,
): { composition: CompositionInputProps; fps: number; resolvedSrc: string } {
  const resolvedSrc = resolveSourceUrl(config.src)
  const { width, height } = getCanvasDimensions(config.orientation, config.resolution)
  const fps = 30
  const templateVisuals = parseTemplateVisualConfig(config.templateConfig)
  const introAsset = templateVisuals?.intro as TemplateAssetRef | null | undefined
  const outroAsset = templateVisuals?.outro as TemplateAssetRef | null | undefined
  const backgroundAsset = templateVisuals?.background as TemplateAssetRef | null | undefined
  // 源视频 fps 未知时用 30；sourceFps 影响帧对齐，这里用 fps 即可
  const sourceFps = fps
  const introDurationFrames = getTemplateDurationFrames(introAsset, fps)
  const outroDurationFrames = getTemplateDurationFrames(outroAsset, fps)

  // 片段在源视频中的帧范围
  const startFrame = Math.round((clip.startMs / 1000) * sourceFps)
  const endFrame = Math.round((clip.endMs / 1000) * sourceFps)
  const clipDurationFrames = Math.max(1, endFrame - startFrame)
  const mainClipFrom = introDurationFrames
  const totalDurationFrames = introDurationFrames + clipDurationFrames + outroDurationFrames

  // contain 适配：按短边缩放
  const backgroundTrackId = `track-background-${Date.now()}`
  const videoTrackId = `track-video-${Date.now()}`
  const captionTrackId = `track-captions-${Date.now()}`
  const logoTrackId = `track-logos-${Date.now()}`

  const backgroundItems: TimelineItem[] = []
  const videoItems: TimelineItem[] = []
  const captionItems: TimelineItem[] = []
  const logoItems: TimelineItem[] = []
  const tracks: TimelineTrack[] = []

  // 视频轨道 + 视频 item
  const backgroundUrl = templateVisuals?.backgroundEnabled !== false ? getTemplateAssetUrl(backgroundAsset) : null
  if (backgroundUrl) {
    backgroundItems.push(
      buildTemplateCompositionImageItem({
        src: backgroundUrl,
        label: backgroundAsset?.fileName ?? 'Template Background',
        trackId: backgroundTrackId,
        from: 0,
        durationInFrames: totalDurationFrames,
        // Canvas renderer 对 image 使用 contain；这里把 item 尺寸扩到 cover，
        // 使横图背景在 9:16 输出里也不会留下黑边。
        transform: buildCoverTransform(width, height, backgroundAsset?.imageWidth, backgroundAsset?.imageHeight),
      }),
    )
  }

  const introUrl = getTemplateAssetUrl(introAsset)
  if (introUrl && introDurationFrames > 0) {
    videoItems.push(
      buildTemplateCompositionVideoItem({
        src: introUrl,
        label: introAsset?.fileName ?? 'Template Intro',
        trackId: videoTrackId,
        from: 0,
        durationInFrames: introDurationFrames,
        width,
        height,
      }),
    )
  }

  const videoItem: VideoItem = {
    id: `clip-video-${Date.now()}`,
    type: 'video',
    trackId: videoTrackId,
    from: mainClipFrom,
    durationInFrames: clipDurationFrames,
    label: clip.title.slice(0, 40),
    src: resolvedSrc,
    sourceStart: startFrame,
    sourceEnd: endFrame,
    sourceDuration: endFrame,
    sourceFps,
    // 9:16 原片放进 9:16 模板时，普通 contain 会刚好铺满画布，导致背景虽在
    // 合成内却永远不可见。品牌背景存在时保留统一安全边距，确保背景成为成品的一部分。
    transform: buildContainTransform(
      width,
      height,
      config.sourceWidth,
      config.sourceHeight,
      templateVisuals?.backgroundEnabled !== false && Boolean(backgroundAsset) ? 0.78 : 1,
    ),
  }

  videoItems.push(videoItem)

  const outroUrl = getTemplateAssetUrl(outroAsset)
  if (outroUrl && outroDurationFrames > 0) {
    videoItems.push(
      buildTemplateCompositionVideoItem({
        src: outroUrl,
        label: outroAsset?.fileName ?? 'Template Outro',
        trackId: videoTrackId,
        from: mainClipFrom + clipDurationFrames,
        durationInFrames: outroDurationFrames,
        width,
        height,
      }),
    )
  }

  // Logo 仅覆盖主片段，保持和品牌模板预览的语义一致。
  const logos =
    templateVisuals?.logoEnabled !== false
      ? ((templateVisuals?.logos as TemplateAssetRef[] | undefined) ?? [])
      : []
  for (const [index, logo] of logos.entries()) {
    const logoUrl = getTemplateAssetUrl(logo)
    if (!logoUrl) continue
    const fallback = getLogoInitialPosition(logo.position)
    logoItems.push(
      buildTemplateCompositionImageItem({
        src: logoUrl,
        label: logo.fileName ?? `Template Logo ${index + 1}`,
        trackId: logoTrackId,
        from: mainClipFrom,
        durationInFrames: clipDurationFrames,
        transform: buildOverlayTransform({
          canvasWidth: width,
          canvasHeight: height,
          x: logo.x,
          y: logo.y,
          widthPercent: logo.width,
          heightPercent: logo.height,
          fallbackWidthPercent: getLogoScaleWidth(logo.scale),
          fallbackLeftPercent: fallback.left,
          fallbackTopPercent: fallback.top,
        }),
      }),
    )
  }

  const segments = words && words.length > 0
    ? buildCaptionSegmentsFromWords(words, clip.startMs, clip.endMs)
    : sentences
        .filter((s) => s.startMs <= clip.endMs + 500 && s.endMs >= clip.startMs - 500)
        .map((s) => ({ text: s.text, start: s.startMs / 1000, end: s.endMs / 1000 }))

  if (config.style && segments.length > 0) {
    // buildCaptionTextItems 用 clip.sourceStart/sourceEnd 做源帧对齐，
    // 所以 segments 的 start/end 必须是源视频绝对秒（不要预先裁剪到片段范围，
    // 函数内部的 overlap 逻辑会自动处理边界裁剪）。
    const styleTemplate = buildCaptionTemplateFromConfig(config)
    captionItems.push(...buildCaptionTextItems({
      mediaId: 'clip-source',
      trackId: captionTrackId,
      segments,
      clip: videoItem,
      timelineFps: fps,
      canvasWidth: width,
      canvasHeight: height,
      styleTemplate,
    }))

    if (captionItems.length > 0) {
      tracks.push({
        id: captionTrackId,
        name: 'C1',
        kind: 'video',
        height: 56,
        locked: false,
        syncLock: true,
        visible: true,
        muted: false,
        solo: false,
        volume: 0,
        order: -1, // 字幕在视频上方（order 越小越上层）
        items: captionItems,
      })
    }
  }

  if (backgroundItems.length > 0) {
    tracks.push({ ...createTrack(backgroundTrackId, 'BG', 'video', 1), items: backgroundItems })
  }
  tracks.push({ ...createTrack(videoTrackId, 'V1', 'video', 0), items: videoItems })
  if (logoItems.length > 0) {
    tracks.push({ ...createTrack(logoTrackId, 'L1', 'video', -2), items: logoItems })
  }

  const composition: CompositionInputProps = {
    fps,
    durationInFrames: totalDurationFrames,
    width,
    height,
    tracks,
    transitions: [],
    backgroundColor:
      typeof templateVisuals?.backgroundColor === 'string' && templateVisuals.backgroundColor
        ? templateVisuals.backgroundColor
        : '#000000',
  }

  return { composition, fps, resolvedSrc }
}

/**
 * 直接渲染单个片段为 MP4 并返回 Blob（不依赖项目/store/workspace）。
 *
 * 调用 renderComposition（WebCodecs 逐帧渲染），适用于从源视频中间裁剪的片段。
 * 中间裁剪无法走 packet-remux 快速路径，会回退到逐帧渲染。
 */
export async function renderClipToMp4(
  clip: ClipCandidate,
  sentences: TranscriptSentence[],
  config: ClipRenderConfig,
  options?: {
    signal?: AbortSignal
    onProgress?: (progress: RenderProgress) => void
    words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>
  },
): Promise<ClientRenderResult> {
  const templateVisuals = parseTemplateVisualConfig(config.templateConfig)
  const [sourceMetadata, introMetadata, outroMetadata, backgroundMetadata] = await Promise.all([
    probeVideoMetadata(resolveSourceUrl(config.src)),
    probeVideoMetadata(getTemplateAssetUrl(templateVisuals?.intro as TemplateAssetRef | null | undefined)),
    probeVideoMetadata(getTemplateAssetUrl(templateVisuals?.outro as TemplateAssetRef | null | undefined)),
    probeImageMetadata(getTemplateAssetUrl(templateVisuals?.background as TemplateAssetRef | null | undefined)),
  ])
  const enrichTemplateAsset = (
    asset: TemplateAssetRef | null | undefined,
    metadata: ProbedVideoMetadata | null,
  ) => asset && metadata && !asset.durationMs
    ? { ...asset, durationMs: metadata.durationMs }
    : asset
  const renderConfig: ClipRenderConfig = {
    ...config,
    sourceWidth: sourceMetadata?.width ?? config.sourceWidth,
    sourceHeight: sourceMetadata?.height ?? config.sourceHeight,
    templateConfig: templateVisuals
      ? {
          ...config.templateConfig,
          intro: enrichTemplateAsset(templateVisuals.intro, introMetadata),
          outro: enrichTemplateAsset(templateVisuals.outro, outroMetadata),
          background: templateVisuals.background && backgroundMetadata
            ? { ...templateVisuals.background, imageWidth: backgroundMetadata.width, imageHeight: backgroundMetadata.height }
            : templateVisuals.background,
        }
      : config.templateConfig,
  }
  const { composition, fps } = buildClipComposition(clip, sentences, renderConfig, options?.words)
  const { width, height } = getCanvasDimensions(config.orientation, config.resolution)

  // 构建 H.264 MP4 导出设置
  const exportSettings: ExtendedExportSettings = {
    codec: 'h264',
    quality: 'high',
    resolution: { width, height },
    mode: 'video',
    videoContainer: 'mp4',
    renderWholeProject: true,
  }

  const clientSettings = mapToClientSettings(exportSettings, fps)
  clientSettings.container = 'mp4'
  clientSettings.mode = 'video'

  const validation = validateSettings(clientSettings)
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  // 检查浏览器编码支持，必要时回退
  const supportedCodecs = await getSupportedCodecs({
    width: clientSettings.resolution.width,
    height: clientSettings.resolution.height,
    bitrate: clientSettings.videoBitrate,
  })
  if (!supportedCodecs.includes(clientSettings.codec)) {
    const fallbackCodec = selectFallbackVideoCodec(supportedCodecs, 'mp4')
    if (!fallbackCodec) {
      throw new Error('当前浏览器不支持可用的视频导出编码。')
    }
    clientSettings.codec = fallbackCodec
    clientSettings.container = getPreferredContainerForCodec(fallbackCodec)
  }

  const result = await renderComposition({
    settings: clientSettings,
    composition,
    onProgress: options?.onProgress ?? (() => {}),
    signal: options?.signal,
  })

  return result
}

/** 生成片段下载文件名 */
export function getClipFileName(clip: ClipCandidate): string {
  const base = clip.title.slice(0, 40).replace(/[\\/:*?"<>|]+/g, '-').trim() || 'clip'
  return `${base}-${formatMs(clip.startMs)}.mp4`
}

/* ------------------------------------------------------------------ */
/* 编辑器路径：创建项目 + 跳转编辑器                                     */
/* ------------------------------------------------------------------ */

/** ensureClipWorkspaceRoot 的结果 */
export type WorkspaceRootStatus =
  | { ok: true }
  | { ok: false; reason: 'no-handle' | 'no-permission'; message: string }

/**
 * 确保 workspace root 已初始化（从 handles-db 读取已保存的目录句柄）。
 *
 * /app/* 路由不在 WorkspaceGate 保护范围内，所以需要在使用存储操作前手动初始化。
 * 如果用户从未选择过工作空间目录（handles-db 无记录），返回 { ok: false, reason: 'no-handle' }，
 * 调用方应弹出 WorkspacePickerDialog 让用户选择。
 */
export async function ensureClipWorkspaceRoot(): Promise<WorkspaceRootStatus> {
  if (getWorkspaceRoot()) {
    return { ok: true }
  }

  const record = await getWorkspaceHandleRecord()
  if (!record) {
    return {
      ok: false,
      reason: 'no-handle',
      message: '尚未选择工作空间目录，请选择一个本地文件夹作为工作空间。',
    }
  }

  const handle = record.handle as FileSystemDirectoryHandle
  const permission = await queryHandlePermission(handle)
  if (permission !== 'granted') {
    return {
      ok: false,
      reason: 'no-permission',
      message: '工作空间目录访问权限未授予，请重新选择工作空间文件夹。',
    }
  }

  setWorkspaceRoot(handle)
  return { ok: true }
}

/**
 * 为单个片段创建编辑器项目：导入源视频 → 裁剪到 [startMs, endMs] → 加字幕 → 保存。
 *
 * 调用前必须确保 workspace root 已初始化（ensureClipWorkspaceRoot）。
 * 返回 projectId 后可 navigate('/editor/$projectId')。
 */
export async function buildClipProjectForEditor(
  clip: ClipCandidate,
  sentences: TranscriptSentence[],
  config: ClipRenderConfig,
  words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>,
  remoteProjectId?: number,
): Promise<{ projectId: string }> {
  const { width, height } = getCanvasDimensions(config.orientation, config.resolution)
  const fps = 30
  const templateVisuals = parseTemplateVisualConfig(config.templateConfig)
  const introAsset = templateVisuals?.intro as TemplateAssetRef | null | undefined
  const outroAsset = templateVisuals?.outro as TemplateAssetRef | null | undefined
  const backgroundAsset = templateVisuals?.background as TemplateAssetRef | null | undefined

  // 1. 创建项目
  const projectCtx = await createAssistantProject({
    name: clip.title.slice(0, 60) || `片段 ${formatMs(clip.startMs)}`,
    width,
    height,
    fps,
  })

  // 2. 导入源视频
  const resolvedSrc = resolveSourceUrl(config.src)
  const imported = await importLocalMedia({
    url: resolvedSrc,
    projectId: projectCtx.projectId,
  })
  const media = imported[0]
  if (!media) {
    throw new Error('源视频导入失败。')
  }

  // 3. 构建轨道和 items 为纯数据（不经过共享 store，避免快照/时序问题导致轨道丢失）
  const tracks = createDefaultClassicTracks()
  const videoTrack = tracks.find((t) => t.kind === 'video')
  if (!videoTrack) {
    throw new Error('无法创建视频轨道。')
  }

  const introMedia = await importTemplateAsset(
    projectCtx.projectId,
    introAsset,
  )
  const outroMedia = await importTemplateAsset(
    projectCtx.projectId,
    outroAsset,
  )
  const backgroundMedia = await importTemplateAsset(
    projectCtx.projectId,
    backgroundAsset,
  )

  const sourceFps = media.fps || fps
  const startFrame = Math.round((clip.startMs / 1000) * sourceFps)
  const endFrame = Math.round((clip.endMs / 1000) * sourceFps)
  const clipDurationFrames = Math.max(1, endFrame - startFrame)
  const introDurationFrames = getTemplateDurationFrames(introAsset, fps, introMedia?.duration)
  const outroDurationFrames = getTemplateDurationFrames(outroAsset, fps, outroMedia?.duration)
  const mainClipFrom = introDurationFrames

  // 4. 构建 video item
  const blobUrl = await mediaLibraryService.getMediaBlobUrl(media.id)
  const videoItem = buildDroppedMediaTimelineItem({
    media,
    mediaId: media.id,
    mediaType: 'video',
    label: clip.title.slice(0, 40),
    timelineFps: fps,
    blobUrl: blobUrl ?? '',
    canvasWidth: width,
    canvasHeight: height,
    placement: { trackId: videoTrack.id, from: mainClipFrom, durationInFrames: clipDurationFrames },
  }) as VideoItem

  videoItem.sourceStart = startFrame
  videoItem.sourceEnd = endFrame
  videoItem.sourceDuration = Math.round(media.duration * sourceFps)
  videoItem.sourceFps = sourceFps
  videoItem.transform = buildContainTransform(
    width,
    height,
    media.width,
    media.height,
    templateVisuals?.backgroundEnabled !== false && Boolean(backgroundAsset) ? 0.78 : 1,
  )

  // 4b. 创建关联的音频 item 放到 A1 轨道上，让用户能看到音频波形。
  //     同时设 embeddedAudioMuted=true 避免视频 item 和音频 item 双重播放。
  const audioTrack = tracks.find((t) => t.kind === 'audio')
  const linkedGroupId = crypto.randomUUID()
  videoItem.linkedGroupId = linkedGroupId
  videoItem.embeddedAudioMuted = true

  const allItems: TimelineItem[] = [videoItem]
  const allTracks = [...tracks]

  if (backgroundMedia && templateVisuals?.backgroundEnabled !== false) {
    const backgroundTrack: TTrack = {
      id: `track-background-${Date.now()}`,
      name: 'BG',
      kind: 'video',
      height: tracks[0]?.height ?? 56,
      locked: false,
      syncLock: true,
      visible: true,
      muted: false,
      solo: false,
      volume: 0,
      order: videoTrack.order + 1,
      items: [],
    }
    allTracks.push(backgroundTrack)
    const backgroundBlobUrl = await mediaLibraryService.getMediaBlobUrl(backgroundMedia.id)
    allItems.push(
      buildTemplateEditorImageItem({
        media: backgroundMedia,
        trackId: backgroundTrack.id,
        label: backgroundMedia.fileName,
        timelineFps: fps,
        blobUrl: backgroundBlobUrl ?? '',
        canvasWidth: width,
        canvasHeight: height,
        from: 0,
        durationInFrames: introDurationFrames + clipDurationFrames + outroDurationFrames,
        transform: buildCoverTransform(width, height, backgroundMedia.width, backgroundMedia.height),
      }),
    )
  }

  // Logo 与导出 Composition 一样，仅覆盖主片段；每个 logo 作为可编辑图片层写入 L1。
  const logos = templateVisuals?.logoEnabled !== false
    ? ((templateVisuals?.logos as TemplateAssetRef[] | undefined) ?? [])
    : []
  if (logos.length > 0) {
    const logoTrack: TTrack = {
      id: `track-logos-${Date.now()}`,
      name: 'L1',
      kind: 'video',
      height: tracks[0]?.height ?? 56,
      locked: false,
      syncLock: true,
      visible: true,
      muted: false,
      solo: false,
      volume: 0,
      order: videoTrack.order - 2,
      items: [],
    }
    const logoItems: ImageItem[] = []

    for (const [index, logo] of logos.entries()) {
      const logoMedia = await importTemplateAsset(projectCtx.projectId, logo)
      if (!logoMedia) continue

      const logoBlobUrl = await mediaLibraryService.getMediaBlobUrl(logoMedia.id)
      const fallback = getLogoInitialPosition(logo.position)
      logoItems.push(
        buildTemplateEditorImageItem({
          media: logoMedia,
          trackId: logoTrack.id,
          label: logo.fileName ?? `Template Logo ${index + 1}`,
          timelineFps: fps,
          blobUrl: logoBlobUrl ?? '',
          canvasWidth: width,
          canvasHeight: height,
          from: mainClipFrom,
          durationInFrames: clipDurationFrames,
          transform: buildOverlayTransform({
            canvasWidth: width,
            canvasHeight: height,
            sourceWidth: logoMedia.width,
            sourceHeight: logoMedia.height,
            x: logo.x,
            y: logo.y,
            widthPercent: logo.width,
            heightPercent: logo.height,
            fallbackWidthPercent: getLogoScaleWidth(logo.scale),
            fallbackLeftPercent: fallback.left,
            fallbackTopPercent: fallback.top,
          }),
        }),
      )
    }

    if (logoItems.length > 0) {
      allTracks.push(logoTrack)
      allItems.push(...logoItems)
    }
  }

  if (introMedia && introDurationFrames > 0) {
    const introBlobUrl = await mediaLibraryService.getMediaBlobUrl(introMedia.id)
    allItems.push(
      buildDroppedMediaTimelineItem({
        media: introMedia,
        mediaId: introMedia.id,
        mediaType: 'video',
        label: introMedia.fileName,
        timelineFps: fps,
        blobUrl: introBlobUrl ?? '',
        canvasWidth: width,
        canvasHeight: height,
        placement: { trackId: videoTrack.id, from: 0, durationInFrames: introDurationFrames },
      }) as VideoItem,
    )
  }

  if (outroMedia && outroDurationFrames > 0) {
    const outroBlobUrl = await mediaLibraryService.getMediaBlobUrl(outroMedia.id)
    allItems.push(
      buildDroppedMediaTimelineItem({
        media: outroMedia,
        mediaId: outroMedia.id,
        mediaType: 'video',
        label: outroMedia.fileName,
        timelineFps: fps,
        blobUrl: outroBlobUrl ?? '',
        canvasWidth: width,
        canvasHeight: height,
        placement: {
          trackId: videoTrack.id,
          from: mainClipFrom + clipDurationFrames,
          durationInFrames: outroDurationFrames,
        },
      }) as VideoItem,
    )
  }

  if (audioTrack) {
    const audioItem: AudioItem = {
      id: crypto.randomUUID(),
      type: 'audio',
      trackId: audioTrack.id,
      from: videoItem.from,
      durationInFrames: videoItem.durationInFrames,
      label: videoItem.label,
      mediaId: videoItem.mediaId,
      originId: videoItem.originId,
      linkedGroupId,
      src: videoItem.src,
      sourceStart: videoItem.sourceStart,
      sourceEnd: videoItem.sourceEnd,
      sourceDuration: videoItem.sourceDuration,
      sourceFps: videoItem.sourceFps,
      trimStart: videoItem.trimStart,
      trimEnd: videoItem.trimEnd,
      volume: 0,
    }
    allItems.push(audioItem)
  }

  // 5. 加字幕（优先用词级时间戳做精细分句，回退到句子级）
  const segments = words && words.length > 0
    ? buildCaptionSegmentsFromWords(words, clip.startMs, clip.endMs)
    : sentences
        .filter((s) => s.startMs <= clip.endMs + 500 && s.endMs >= clip.startMs - 500)
        .map((s) => ({ text: s.text, start: s.startMs / 1000, end: s.endMs / 1000 }))

  if (config.style && segments.length > 0) {
    // 字幕轨放在视频轨上方（order 越小越上层）。
    // createDefaultClassicTracks 的 V1 order=0，所以字幕轨用 -1。
    const captionTrack: TTrack = {
      id: `track-captions-${Date.now()}`,
      name: 'C1',
      kind: 'video',
      height: tracks[0]?.height ?? 56,
      locked: false,
      syncLock: true,
      visible: true,
      muted: false,
      solo: false,
      volume: 0,
      order: videoTrack.order - 1,
      items: [],
    }
    allTracks.push(captionTrack)

    const styleTemplate = buildCaptionTemplateFromConfig(config)
    const captionItems = buildCaptionTextItems({
      mediaId: media.id,
      trackId: captionTrack.id,
      segments,
      clip: videoItem,
      timelineFps: fps,
      canvasWidth: width,
      canvasHeight: height,
      styleTemplate,
    })

    allItems.push(...captionItems)
  }

  // 6. 直接持久化（不经过共享 store，避免 clearTimeline/setTracks/addItems 的快照时序问题）
  await updateProject(projectCtx.projectId, {
    metadata: {
      width,
      height,
      fps,
      ...(typeof templateVisuals?.backgroundColor === 'string' && templateVisuals.backgroundColor
        ? { backgroundColor: templateVisuals.backgroundColor }
        : {}),
    },
    timeline: {
      tracks: allTracks as ProjectTimeline['tracks'],
      items: allItems as ProjectTimeline['items'],
      masterBusDb: 0,
      currentFrame: 0,
      zoomLevel: 1,
      scrollPosition: 0,
    },
    updatedAt: Date.now(),
    ...(typeof config.brandTemplateId === 'number' ? { brandTemplateId: config.brandTemplateId } : {}),
    ...(config.brandTemplateName ? { brandTemplateName: config.brandTemplateName } : {}),
    ...(config.templateConfig ? { brandTemplateConfigSnapshot: structuredClone(config.templateConfig) } : {}),
    ...(remoteProjectId && { remoteProjectId }),
  })

  return { projectId: projectCtx.projectId }
}

/**
 * 从单个源视频直接创建一个本地视频编辑项目，并自动把素材放到时间线。
 *
 * 适用于首页“编辑视频”入口：用户已经提供了链接素材，希望直接进入专业编辑器。
 */
export async function buildSourceProjectForEditor(
  config: SourceEditorProjectConfig,
): Promise<{ projectId: string }> {
  const fallbackWidth = 1920
  const fallbackHeight = 1080
  const fallbackFps = 30

  const projectCtx = await createAssistantProject({
    name: deriveProjectTitleFromSource(config.title || config.src),
    width: fallbackWidth,
    height: fallbackHeight,
    fps: fallbackFps,
  })

  const imported = await importLocalMedia({
    url: resolveSourceUrl(config.src),
    projectId: projectCtx.projectId,
  })
  const media = imported[0]
  if (!media) {
    throw new Error('源视频导入失败。')
  }

  const width = media.width || fallbackWidth
  const height = media.height || fallbackHeight
  const fps = media.fps || fallbackFps
  const durationInFrames = Math.max(1, Math.round((media.duration || 1) * fps))

  const tracks = createDefaultClassicTracks()
  const videoTrack = tracks.find((t) => t.kind === 'video')
  if (!videoTrack) {
    throw new Error('无法创建视频轨道。')
  }

  const blobUrl = await mediaLibraryService.getMediaBlobUrl(media.id)
  const videoItem = buildDroppedMediaTimelineItem({
    media,
    mediaId: media.id,
    mediaType: 'video',
    label: media.fileName.slice(0, 40),
    timelineFps: fps,
    blobUrl: blobUrl ?? '',
    canvasWidth: width,
    canvasHeight: height,
    placement: { trackId: videoTrack.id, from: 0, durationInFrames },
  }) as VideoItem

  videoItem.sourceStart = 0
  videoItem.sourceEnd = durationInFrames
  videoItem.sourceDuration = durationInFrames
  videoItem.sourceFps = fps

  const allItems: TimelineItem[] = [videoItem]
  const audioTrack = tracks.find((t) => t.kind === 'audio')

  if (audioTrack) {
    const linkedGroupId = crypto.randomUUID()
    videoItem.linkedGroupId = linkedGroupId
    videoItem.embeddedAudioMuted = true

    const audioItem: AudioItem = {
      id: crypto.randomUUID(),
      type: 'audio',
      trackId: audioTrack.id,
      from: videoItem.from,
      durationInFrames: videoItem.durationInFrames,
      label: videoItem.label,
      mediaId: videoItem.mediaId,
      originId: videoItem.originId,
      linkedGroupId,
      src: videoItem.src,
      sourceStart: videoItem.sourceStart,
      sourceEnd: videoItem.sourceEnd,
      sourceDuration: videoItem.sourceDuration,
      sourceFps: videoItem.sourceFps,
      trimStart: videoItem.trimStart,
      trimEnd: videoItem.trimEnd,
      volume: 0,
    }
    allItems.push(audioItem)
  }

  await updateProject(projectCtx.projectId, {
    metadata: {
      width,
      height,
      fps,
      backgroundColor: '#000000',
    },
    timeline: {
      tracks: tracks as ProjectTimeline['tracks'],
      items: allItems as ProjectTimeline['items'],
      masterBusDb: 0,
      currentFrame: 0,
      zoomLevel: 1,
      scrollPosition: 0,
    },
    updatedAt: Date.now(),
  })

  return { projectId: projectCtx.projectId }
}

/**
 * 从本地文件句柄直接创建一个视频编辑项目。
 *
 * 相比“先上传再创建项目”，这条链路更符合专业编辑器的本地优先模式。
 */
export async function buildFileProjectForEditor(
  config: FileEditorProjectConfig,
): Promise<{ projectId: string }> {
  const file = await config.handle.getFile()
  const fallbackWidth = 1920
  const fallbackHeight = 1080
  const fallbackFps = 30

  const projectCtx = await createAssistantProject({
    name: deriveProjectTitleFromSource(config.title || file.name),
    width: fallbackWidth,
    height: fallbackHeight,
    fps: fallbackFps,
  })

  const media = await mediaLibraryService.importMediaWithHandle(config.handle, projectCtx.projectId)

  const width = media.width || fallbackWidth
  const height = media.height || fallbackHeight
  const fps = media.fps || fallbackFps
  const durationInFrames = Math.max(1, Math.round((media.duration || 1) * fps))

  const tracks = createDefaultClassicTracks()
  const videoTrack = tracks.find((t) => t.kind === 'video')
  if (!videoTrack) {
    throw new Error('无法创建视频轨道。')
  }

  const blobUrl = await mediaLibraryService.getMediaBlobUrl(media.id)
  const videoItem = buildDroppedMediaTimelineItem({
    media,
    mediaId: media.id,
    mediaType: 'video',
    label: media.fileName.slice(0, 40),
    timelineFps: fps,
    blobUrl: blobUrl ?? '',
    canvasWidth: width,
    canvasHeight: height,
    placement: { trackId: videoTrack.id, from: 0, durationInFrames },
  }) as VideoItem

  videoItem.sourceStart = 0
  videoItem.sourceEnd = durationInFrames
  videoItem.sourceDuration = durationInFrames
  videoItem.sourceFps = fps

  const allItems: TimelineItem[] = [videoItem]
  const audioTrack = tracks.find((t) => t.kind === 'audio')

  if (audioTrack) {
    const linkedGroupId = crypto.randomUUID()
    videoItem.linkedGroupId = linkedGroupId
    videoItem.embeddedAudioMuted = true

    const audioItem: AudioItem = {
      id: crypto.randomUUID(),
      type: 'audio',
      trackId: audioTrack.id,
      from: videoItem.from,
      durationInFrames: videoItem.durationInFrames,
      label: videoItem.label,
      mediaId: videoItem.mediaId,
      originId: videoItem.originId,
      linkedGroupId,
      src: videoItem.src,
      sourceStart: videoItem.sourceStart,
      sourceEnd: videoItem.sourceEnd,
      sourceDuration: videoItem.sourceDuration,
      sourceFps: videoItem.sourceFps,
      trimStart: videoItem.trimStart,
      trimEnd: videoItem.trimEnd,
      volume: 0,
    }
    allItems.push(audioItem)
  }

  await updateProject(projectCtx.projectId, {
    metadata: {
      width,
      height,
      fps,
      backgroundColor: '#000000',
    },
    timeline: {
      tracks: tracks as ProjectTimeline['tracks'],
      items: allItems as ProjectTimeline['items'],
      masterBusDb: 0,
      currentFrame: 0,
      zoomLevel: 1,
      scrollPosition: 0,
    },
    updatedAt: Date.now(),
  })

  return { projectId: projectCtx.projectId }
}
